/**
 * Pure per-persona nav data - no dependency on Sidebar.tsx, AppShell.tsx,
 * WorkspaceShell.tsx, ReporterShell.tsx or RoleGuideProvider.tsx as VALUES
 * (only NavSection as a type, which TypeScript erases at compile time and so
 * never creates a runtime edge). Kept in its own leaf module specifically so
 * domain/roleGuide.ts can source real nav labels/routes/icons for its
 * "where to find things" content without creating a circular import:
 * Sidebar -> RoleGuideProvider -> roleGuide -> (this file only) would
 * otherwise become Sidebar -> ... -> AppShell -> Sidebar if this data lived
 * inside AppShell.tsx/WorkspaceShell.tsx/ReporterShell.tsx instead, since
 * those files import Sidebar.tsx (or, for ReporterShell, RoleGuideProvider)
 * as real values.
 */

import {
  LayoutDashboard,
  CheckSquare,
  BarChart3,
  ScrollText,
  FileText,
  ShieldCheck,
  Radar,
  Inbox,
  Plus,
  Camera,
  User,
  Boxes,
  CalendarRange,
  Wallet,
  Settings,
} from "lucide-react";
import type { NavSection, NavItem } from "./Sidebar";

// Oversight navigation (deal-grid redesign): the home is now the segregated deal-card
// grid (replacing the old over-aggregated Portfolio hub). Cross-cutting destinations
// (Signals, Approvals, Controls, Insights, Reports) stay pinned; detail/analysis
// surfaces (a project, the map, a site visit, KYC, a report) are reached by drilling
// into a deal or switching a tab, never pinned to the sidebar.
export const stratusNav: NavSection[] = [
  {
    items: [
      {
        to: "/stratus",
        label: "All deals",
        icon: LayoutDashboard,
        end: true,
        description: "Every programme you oversee - budget committed and burn at a glance.",
      },
      {
        to: "/stratus/signals",
        label: "Signals",
        icon: Radar,
        description: "Exceptions and reconciliation flags that breach your materiality thresholds, in one inbox.",
      },
      {
        to: "/stratus/projects",
        label: "Projects",
        icon: Boxes,
        description: "Every individual project across every deal you oversee, in one list.",
      },
      {
        to: "/stratus/approvals",
        label: "Approvals",
        icon: CheckSquare,
        description: "Confirm submitted evidence and route confirmed milestones on to the funder.",
      },
      {
        to: "/stratus/controls",
        label: "Controls",
        icon: ShieldCheck,
        description: "Assurance, compliance, contractor KYC, and who has access to what.",
      },
      {
        to: "/stratus/insights",
        label: "Insights",
        icon: BarChart3,
        description: "Spend pace and cross-programme comparison analytics.",
      },
      {
        to: "/stratus/reports",
        label: "Reports",
        icon: FileText,
        description: "Generated programme and funder reports.",
      },
    ],
  },
  {
    items: [
      {
        to: "/stratus/audit",
        label: "Audit trail",
        icon: ScrollText,
        description: "Every governed action across the programme, unrestricted.",
      },
    ],
  },
];

export const funderNav: NavSection[] = [
  {
    items: [
      {
        to: "/funder",
        label: "Your deals",
        icon: LayoutDashboard,
        end: true,
        description: "Programmes you fund - portfolio summary and what's in preparation.",
      },
      {
        to: "/funder/projects",
        label: "Projects",
        icon: Boxes,
        description: "Every individual project across the deals shared with you, in one list.",
      },
      {
        to: "/funder/approvals",
        label: "Approvals",
        icon: CheckSquare,
        description: "Milestone disbursements routed to you by Stratus, awaiting your release.",
      },
      {
        to: "/funder/insights",
        label: "Insights",
        icon: BarChart3,
        description: "Spend and disbursement analytics across your funded programmes.",
      },
      {
        to: "/funder/reports",
        label: "Reports",
        icon: FileText,
        description: "Generated reports for the programmes you fund.",
      },
    ],
  },
  {
    items: [
      {
        to: "/funder/audit",
        label: "Audit trail",
        icon: ScrollText,
        description: "The disbursement and approval history for your programmes.",
      },
    ],
  },
];

// Reporter's flat nav list (no sections - the mobile shell renders these as
// both a desktop side rail and a bottom tab bar, see ReporterShell.tsx).
export const reporterNav: NavItem[] = [
  {
    to: "/reporter",
    label: "Inbox",
    icon: Inbox,
    end: true,
    description: "Every spend ask you've raised, and its current status.",
  },
  {
    to: "/reporter/raise",
    label: "Raise",
    icon: Plus,
    end: false,
    description: "Raise a new spend ask for your assigned project.",
  },
  {
    to: "/reporter/evidence",
    label: "Evidence",
    icon: Camera,
    end: false,
    description: "Submit evidence - a note plus a photo or document - for an approved ask.",
  },
  {
    to: "/reporter/profile",
    label: "Profile",
    icon: User,
    end: false,
    description: "Your details, offline sync settings, and sign out.",
  },
];

// IA redesign: a contractor and an oversight viewer each get a slim, parallel sidebar.
// Detail (a project, project health) and reference surfaces (authority, connections,
// participants, audit) are reached by drilling into a destination or a tab, not pinned here.
export function workspaceNavSections(base: string, isContractor: boolean): NavSection[] {
  const primary = [
    {
      to: base,
      label: isContractor ? "My dashboard" : "Programme overview",
      icon: LayoutDashboard,
      end: true,
      description: isContractor
        ? "Your project's budget, schedule, and what needs attention."
        : "This programme's budget, schedule, and what needs attention.",
    },
    {
      to: `${base}/projects`,
      label: isContractor ? "My projects" : "Projects",
      icon: Boxes,
      description: isContractor
        ? "Every project and sub-project you lead or belong to."
        : "Every project and sub-project in this programme.",
    },
    {
      to: `${base}/plan`,
      label: "Planning",
      icon: CalendarRange,
      description: "Milestones and schedule.",
    },
    {
      to: `${base}/approvals`,
      label: "Approvals",
      icon: CheckSquare,
      description: isContractor
        ? "Spend asks raised by your team, waiting for your approval."
        : "Spend asks and their approval status across this programme.",
    },
    {
      to: `${base}/finance`,
      label: "Finance & controls",
      icon: Wallet,
      description: "Spend thresholds, bank connections, and financial controls.",
    },
    {
      to: `${base}/analytics`,
      label: "Insights",
      icon: BarChart3,
      description: "Spend pace and budget analytics.",
    },
  ];
  return [
    { items: primary },
    {
      items: [
        {
          to: `${base}/settings`,
          label: "Settings",
          icon: Settings,
          description: "Team members and audit history.",
        },
      ],
    },
  ];
}
